"use client"

import { useState, useEffect } from "react"
import { Package, Plus, Edit, Trash2, Search, Upload, X, Check, AlertCircle, List } from "lucide-react"
import "./AdminDashboard.css"

const ProductManagementDashboard = () => {
  // State management
  const [activeOperation, setActiveOperation] = useState("") // 'create', 'update', 'delete', 'view'
  const [activeSection, setActiveSection] = useState("") // 'product', 'news', 'category'
  // Form data states
  const [productData, setProductData] = useState({
    name: "",
    description: "",
    price: "",
    category: "",
    image: null,
    tags: "", // Added tags field
  })
  const [newsData, setNewsData] = useState({
    headline: "",
    description: "",
    photo: null,
  })
  const [categoryData, setCategoryData] = useState({
    categoryName: "",
  })
  // ID states
  const [productId, setProductId] = useState("")
  const [newsId, setNewsId] = useState("")
  const [categoryId, setCategoryId] = useState("")
  // UI states
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState({ type: "", description: "" })
  // Fetched data states
  const [fetchedProduct, setFetchedProduct] = useState(null)
  const [fetchedNews, setFetchedNews] = useState(null)
  const [fetchedCategory, setFetchedCategory] = useState(null)
  const [categories, setCategories] = useState([])
  // API configuration
  const API_BASE_URL = "https://imperia-backend.onrender.com/api"
  const BEARER_TOKEN = "YWRtaW5AZXhhbXBsZS5jb206c3VwZXJzZWN1cmU="
  // Helper functions for API headers
  const getAuthHeaders = () => ({
    Authorization: `Bearer ${BEARER_TOKEN}`,
    "Content-Type": "application/json",
  })
  const getAuthHeadersForFormData = () => ({
    Authorization: `Bearer ${BEARER_TOKEN}`,
  })
  // Reset all form fields
  const resetForm = () => {
    setProductData({
      name: "",
      description: "",
      price: "",
      category: "",
      image: null,
      tags: "", // Reset tags field
    })
    setNewsData({
      headline: "",
      description: "",
      photo: null,
    })
    setCategoryData({
      categoryName: "",
    })
    setProductId("")
    setNewsId("")
    setCategoryId("")
    setFetchedProduct(null)
    setFetchedNews(null)
    setFetchedCategory(null)
    setMessage({ type: "", description: "" })
  }
  // Display messages with timeout
  const showMessage = (type, description) => {
    setMessage({ type, description })
    setTimeout(() => setMessage({ type: "", description: "" }), 7000)
  }
  // Input change handlers
  const handleProductInputChange = (e) => {
    const { name, value } = e.target
    setProductData((prev) => ({ ...prev, [name]: value }))
  }
  const handleNewsInputChange = (e) => {
    const { name, value } = e.target
    setNewsData((prev) => ({ ...prev, [name]: value }))
  }
  const handleCategoryInputChange = (e) => {
    const { name, value } = e.target
    setCategoryData((prev) => ({ ...prev, [name]: value }))
  }
  const handleProductImageChange = (e) => {
    setProductData((prev) => ({ ...prev, image: e.target.files[0] }))
  }
  const handleNewsPhotoChange = (e) => {
    setNewsData((prev) => ({ ...prev, photo: e.target.files[0] }))
  }
  // Fetch all categories on component mount
  useEffect(() => {
    fetchAllCategories()
  }, [])

  // Effect to pre-fill product data when fetchedProduct changes for update operation
  useEffect(() => {
    if (activeOperation === "update" && activeSection === "product" && fetchedProduct) {
      setProductData({
        name: fetchedProduct.name || "",
        description: fetchedProduct.description || "",
        price: fetchedProduct.price || "",
        category: fetchedProduct.category || "",
        tags: fetchedProduct.tags || "", // Pre-fill tags
        image: null, // Do not pre-fill image input
      })
    }
  }, [fetchedProduct, activeOperation, activeSection])

  // Category operations
  const fetchAllCategories = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}/categories`, {
        method: "GET",
        headers: getAuthHeaders(),
      })
      const result = await response.json()
      if (response.ok) {
        // Use the correct ID field that matches your backend expectations
        const categoriesWithConsistentIds = (result.data || []).map((cat) => ({
          ...cat,
          // Use the ID field that your backend expects when creating products
          id: cat.categoryId, // Explicitly use categoryId since that's what your backend wants
        }))
        setCategories(categoriesWithConsistentIds)
      } else {
        showMessage("error", result.message || "Failed to fetch categories")
      }
    } catch (error) {
      showMessage("error", "Network error: " + error.message)
    } finally {
      setLoading(false)
    }
  }
  const createCategory = async () => {
    const categoryName = categoryData.categoryName.trim()
    if (!categoryName) {
      showMessage("error", "Category name is required")
      return
    }
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}/categories`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          categoryName: categoryName,
        }),
      })
      const result = await response.json()
      if (response.ok) {
        showMessage("success", result.message || "Category created successfully")
        resetForm()
        fetchAllCategories()
      } else {
        showMessage("error", result.message || "Failed to create category")
      }
    } catch (error) {
      showMessage("error", "Network error: " + error.message)
    } finally {
      setLoading(false)
    }
  }
  const fetchCategoryById = async () => {
    if (!categoryId) {
      showMessage("error", "Please enter a category ID")
      return
    }
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}/categories/${categoryId}`, {
        method: "GET",
        headers: getAuthHeaders(),
      })
      const result = await response.json()
      if (response.ok) {
        setFetchedCategory(result.data)
        showMessage("success", "Category fetched successfully")
      } else {
        showMessage("error", result.message || "Category not found")
        setFetchedCategory(null)
      }
    } catch (error) {
      showMessage("error", "Network error: " + error.message)
      setFetchedCategory(null)
    } finally {
      setLoading(false)
    }
  }
  const updateCategory = async () => {
    if (!categoryId) {
      showMessage("error", "Please enter a category ID")
      return
    }
    const categoryName = categoryData.categoryName.trim()
    if (!categoryName) {
      showMessage("error", "Category name is required")
      return
    }
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}/categories/${categoryId}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          categoryName: categoryName,
        }),
      })
      const result = await response.json()
      if (response.ok) {
        showMessage("success", result.message || "Category updated successfully")
        fetchAllCategories()
        setFetchedCategory(result.data)
      } else {
        showMessage("error", result.message || "Failed to update category")
      }
    } catch (error) {
      showMessage("error", "Network error: " + error.message)
    } finally {
      setLoading(false)
    }
  }
  const deleteCategory = async () => {
    if (!categoryId) {
      showMessage("error", "Please enter a category ID")
      return
    }
    if (!window.confirm("Are you sure you want to delete this category and all its products?")) {
      return
    }
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}/categories/${categoryId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      })
      const result = await response.json()
      if (response.ok) {
        showMessage("success", result.message || "Category deleted successfully")
        resetForm()
        fetchAllCategories()
      } else {
        showMessage("error", result.message || "Failed to delete category")
      }
    } catch (error) {
      showMessage("error", "Network error: " + error.message)
    } finally {
      setLoading(false)
    }
  }
  // Product operations
  const createProduct = async () => {
    // Validate all required fields, including tags
    if (
      !productData.name ||
      !productData.description ||
      !productData.price ||
      !productData.category ||
      !productData.tags
    ) {
      showMessage("error", "Please fill in all required fields")
      return
    }
    // Find the selected category
    const selectedCategory = categories.find((cat) => cat.id === productData.category)
    // Verify the category exists and has the required ID field
    if (!selectedCategory) {
      showMessage("error", "Selected category is invalid. Please refresh the categories.")
      return
    }
    // Determine which ID field to use (categoryId or _id)
    const categoryIdToSend = selectedCategory.categoryId || selectedCategory._id
    if (!categoryIdToSend) {
      showMessage("error", "Category ID is missing. Please select a valid category.")
      return
    }
    setLoading(true)
    try {
      const formData = new FormData()
      formData.append("name", productData.name)
      formData.append("description", productData.description)
      formData.append("price", productData.price)
      formData.append("category", categoryIdToSend) // Use the correct ID field
      formData.append("tags", productData.tags) // Append tags
      if (productData.image) {
        formData.append("image", productData.image)
      }
      // Debugging: Log what we're sending
      console.log("Creating product with data:", {
        name: productData.name,
        description: productData.description,
        price: productData.price,
        category: categoryIdToSend,
        tags: productData.tags, // Log tags
        image: productData.image ? productData.image.name : "none",
      })
      const response = await fetch(`${API_BASE_URL}/products`, {
        method: "POST",
        headers: getAuthHeadersForFormData(),
        body: formData,
      })
      const result = await response.json()
      if (response.ok) {
        showMessage("success", result.message || "Product created successfully")
        resetForm()
      } else {
        // Enhanced error handling
        let errorMessage = result.message || "Failed to create product"
        if (result.errors) {
          errorMessage += ": " + Object.values(result.errors).join(", ")
        }
        showMessage("error", errorMessage)
        // Log detailed error for debugging
        console.error("API Error Response:", result)
      }
    } catch (error) {
      console.error("Network Error:", error)
      showMessage("error", `Network error: ${error.message || "Failed to connect to server"}`)
    } finally {
      setLoading(false)
    }
  }
  const fetchProductById = async () => {
    if (!productId) {
      showMessage("error", "Please enter a product ID")
      return
    }
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}/products/${productId}`, {
        method: "GET",
        headers: getAuthHeaders(),
      })
      const result = await response.json()
      if (response.ok) {
        setFetchedProduct(result.data)
        showMessage("success", "Product fetched successfully")
      } else {
        showMessage("error", result.message || "Product not found")
        setFetchedProduct(null)
      }
    } catch (error) {
      showMessage("error", "Network error: " + error.message)
      setFetchedProduct(null)
    } finally {
      setLoading(false)
    }
  }
  const updateProduct = async () => {
    if (!productId) {
      showMessage("error", "Please enter a product ID")
      return
    }
    setLoading(true)
    try {
      const formData = new FormData()
      if (productData.name) formData.append("name", productData.name)
      if (productData.description) formData.append("description", productData.description)
      if (productData.price) formData.append("price", productData.price)
      if (productData.category) formData.append("category", productData.category)
      if (productData.tags) formData.append("tags", productData.tags) // Append tags
      if (productData.image) formData.append("image", productData.image)
      const response = await fetch(`${API_BASE_URL}/products/${productId}`, {
        method: "PUT",
        headers: getAuthHeadersForFormData(),
        body: formData,
      })
      const result = await response.json()
      if (response.ok) {
        showMessage("success", result.message || "Product updated successfully")
        setFetchedProduct(result.data)
      } else {
        showMessage("error", result.message || "Failed to update product")
      }
    } catch (error) {
      showMessage("error", "Network error: " + error.message)
    } finally {
      setLoading(false)
    }
  }
  const deleteProduct = async () => {
    if (!productId) {
      showMessage("error", "Please enter a product ID")
      return
    }
    if (!window.confirm("Are you sure you want to delete this product?")) {
      return
    }
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}/products/${productId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      })
      const result = await response.json()
      if (response.ok) {
        showMessage("success", result.message || "Product deleted successfully")
        resetForm()
      } else {
        showMessage("error", result.message || "Failed to delete product")
      }
    } catch (error) {
      showMessage("error", "Network error: " + error.message)
    } finally {
      setLoading(false)
    }
  }
  // News operations
  const fetchNewsById = async () => {
    if (!newsId) {
      showMessage("error", "Please enter a news ID")
      return
    }
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}/news/${newsId}`, {
        method: "GET",
        headers: getAuthHeaders(),
      })
      const result = await response.json()
      if (response.ok) {
        setFetchedNews(result.data)
        showMessage("success", "News fetched successfully")
      } else {
        showMessage("error", result.message || "News not found")
        setFetchedNews(null)
      }
    } catch (error) {
      showMessage("error", "Network error: " + error.message)
      setFetchedNews(null)
    } finally {
      setLoading(false)
    }
  }
  const createNews = async () => {
    if (!newsData.headline || !newsData.description) {
      showMessage("error", "Please fill in headline and description")
      return
    }
    setLoading(true)
    try {
      const formData = new FormData()
      formData.append("headline", newsData.headline)
      formData.append("description", newsData.description)
      if (newsData.photo) {
        formData.append("photo", newsData.photo)
      }
      const response = await fetch(`${API_BASE_URL}/news`, {
        method: "POST",
        headers: getAuthHeadersForFormData(),
        body: formData,
      })
      const result = await response.json()
      if (response.ok) {
        showMessage("success", result.message || "News created successfully")
        resetForm()
      } else {
        showMessage("error", result.message || "Failed to create news")
      }
    } catch (error) {
      showMessage("error", "Network error: " + error.message)
    } finally {
      setLoading(false)
    }
  }
  const updateNews = async () => {
    if (!newsId) {
      showMessage("error", "Please enter a news ID")
      return
    }
    setLoading(true)
    try {
      const formData = new FormData()
      if (newsData.headline) formData.append("headline", newsData.headline)
      if (newsData.description) formData.append("description", newsData.description)
      if (newsData.photo) formData.append("photo", newsData.photo)
      const response = await fetch(`${API_BASE_URL}/news/${newsId}`, {
        method: "PUT",
        headers: getAuthHeadersForFormData(),
        body: formData,
      })
      const result = await response.json()
      if (response.ok) {
        showMessage("success", result.message || "News updated successfully")
        setFetchedNews(result.data)
      } else {
        showMessage("error", result.message || "Failed to update news")
      }
    } catch (error) {
      showMessage("error", "Network error: " + error.message)
    } finally {
      setLoading(false)
    }
  }
  const deleteNews = async () => {
    if (!newsId) {
      showMessage("error", "Please enter a news ID")
      return
    }
    if (!window.confirm("Are you sure you want to delete this news?")) {
      return
    }
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}/news/${newsId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      })
      const result = await response.json()
      if (response.ok) {
        showMessage("success", result.message || "News deleted successfully")
        resetForm()
      } else {
        showMessage("error", result.message || "Failed to delete news")
      }
    } catch (error) {
      showMessage("error", "Network error: " + error.message)
    } finally {
      setLoading(false)
    }
  }
  // Handle operation button clicks
  const handleOperationClick = (operation, section) => {
    setActiveOperation(operation)
    setActiveSection(section)
    resetForm()
  }
  return (
    <div className="dashboard-container">
      <div className="dashboard-wrapper">
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-description">
            <div className="header-icon">
              <Package size={32} />
            </div>
            <div>
              <h1 className="header-title">Admin Management Dashboard</h1>
              <p className="header-subtitle">Manage your products, categories, and news with CRUD operations</p>
            </div>
          </div>
        </div>
        {/* Message Display */}
        {message.description && (
          <div className="message-container">
            <div className={`message ${message.type}`}>
              {message.type === "success" ? <Check size={20} /> : <AlertCircle size={20} />}
              <span>{message.description}</span>
            </div>
          </div>
        )}
        {/* Product Operations */}
        <div className="operations-container">
          <h2 className="operations-title">Product Management</h2>
          <div className="operations-grid">
            <button
              onClick={() => handleOperationClick("create", "product")}
              className={`operation-button ${activeOperation === "create" && activeSection === "product" ? "active create" : ""} create`}
            >
              <Plus className="operation-icon" size={24} />
              <span className="operation-text">Create Product</span>
            </button>
            <button
              onClick={() => handleOperationClick("view", "product")}
              className={`operation-button ${activeOperation === "view" && activeSection === "product" ? "active view" : ""} view`}
            >
              <Search className="operation-icon" size={24} />
              <span className="operation-text">View Product</span>
            </button>
            <button
              onClick={() => handleOperationClick("update", "product")}
              className={`operation-button ${activeOperation === "update" && activeSection === "product" ? "active update" : ""} update`}
            >
              <Edit className="operation-icon" size={24} />
              <span className="operation-text">Update Product</span>
            </button>
            <button
              onClick={() => handleOperationClick("delete", "product")}
              className={`operation-button ${activeOperation === "delete" && activeSection === "product" ? "active delete" : ""} delete`}
            >
              <Trash2 className="operation-icon" size={24} />
              <span className="operation-text">Delete Product</span>
            </button>
          </div>
        </div>
        {/* Category Operations */}
        <div className="operations-container">
          <h2 className="operations-title">Category Management</h2>
          <div className="operations-grid">
            <button
              onClick={() => handleOperationClick("create", "category")}
              className={`operation-button ${activeOperation === "create" && activeSection === "category" ? "active create" : ""} create`}
            >
              <Plus className="operation-icon" size={24} />
              <span className="operation-text">Create Category</span>
            </button>
            <button
              onClick={() => handleOperationClick("view", "category")}
              className={`operation-button ${activeOperation === "view" && activeSection === "category" ? "active view" : ""} view`}
            >
              <List className="operation-icon" size={24} />
              <span className="operation-text">View Category</span>
            </button>
            <button
              onClick={() => handleOperationClick("update", "category")}
              className={`operation-button ${activeOperation === "update" && activeSection === "category" ? "active update" : ""} update`}
            >
              <Edit className="operation-icon" size={24} />
              <span className="operation-text">Update Category</span>
            </button>
            <button
              onClick={() => handleOperationClick("delete", "category")}
              className={`operation-button ${activeOperation === "delete" && activeSection === "category" ? "active delete" : ""} delete`}
            >
              <Trash2 className="operation-icon" size={24} />
              <span className="operation-text">Delete Category</span>
            </button>
          </div>
        </div>
        {/* News Operations */}
        <div className="operations-container">
          <h2 className="operations-title">News Management</h2>
          <div className="operations-grid">
            <button
              onClick={() => handleOperationClick("create", "news")}
              className={`operation-button ${activeOperation === "create" && activeSection === "news" ? "active create" : ""} create`}
            >
              <Plus className="operation-icon" size={24} />
              <span className="operation-text">Create News</span>
            </button>
            <button
              onClick={() => handleOperationClick("view", "news")}
              className={`operation-button ${activeOperation === "view" && activeSection === "news" ? "active view" : ""} view`}
            >
              <Search className="operation-icon" size={24} />
              <span className="operation-text">View News</span>
            </button>
            <button
              onClick={() => handleOperationClick("update", "news")}
              className={`operation-button ${activeOperation === "update" && activeSection === "news" ? "active update" : ""} update`}
            >
              <Edit className="operation-icon" size={24} />
              <span className="operation-text">Update News</span>
            </button>
            <button
              onClick={() => handleOperationClick("delete", "news")}
              className={`operation-button ${activeOperation === "delete" && activeSection === "news" ? "active delete" : ""} delete`}
            >
              <Trash2 className="operation-icon" size={24} />
              <span className="operation-text">Delete News</span>
            </button>
          </div>
        </div>
        {/* Operation Forms */}
        {activeOperation && activeSection && (
          <div className="form-container">
            <div className="form-header">
              <h2 className="form-title">
                {activeOperation.charAt(0).toUpperCase() + activeOperation.slice(1)} {activeSection}
              </h2>
              <button
                onClick={() => {
                  setActiveOperation("")
                  setActiveSection("")
                }}
                className="close-button"
              >
                <X size={24} />
              </button>
            </div>
            {/* Product Forms */}
            {activeSection === "product" && (
              <>
                {/* Create Product Form */}
                {activeOperation === "create" && (
                  <div>
                    <div className="form-group">
                      <label className="form-label">
                        Product Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={productData.name}
                        onChange={handleProductInputChange}
                        className="form-input"
                        placeholder="Enter product name"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        Description <span className="required">*</span>
                      </label>
                      <textarea
                        name="description"
                        value={productData.description}
                        onChange={handleProductInputChange}
                        rows={4}
                        className="form-textarea"
                        placeholder="Enter product description"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        Tags (comma-separated) <span className="required">*</span>
                      </label>
                      <textarea
                        name="tags"
                        value={productData.tags}
                        onChange={handleProductInputChange}
                        rows={4}
                        className="form-textarea"
                        placeholder="Enter tags (e.g., automotive, high-pressure, industrial)"
                      />
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">
                          Price <span className="required">*</span>
                        </label>
                        <input
                          type="number"
                          name="price"
                          value={productData.price}
                          onChange={handleProductInputChange}
                          step="0.01"
                          className="form-input"
                          placeholder="Enter price"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          Category <span className="required">*</span>
                        </label>
                        <select
                          name="category"
                          value={productData.category}
                          onChange={handleProductInputChange}
                          className="form-select"
                          required
                        >
                          <option value="">Select category</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.categoryName} ({cat.categoryId || cat._id})
                            </option>
                          ))}
                        </select>
                        {!productData.category && <div className="error-message">Please select a category</div>}
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Product Image</label>
                      <div className="file-upload-container">
                        <input
                          type="file"
                          onChange={handleProductImageChange}
                          accept="image/*"
                          style={{ display: "none" }}
                          id="image-upload"
                        />
                        <label htmlFor="image-upload" className="file-upload-label">
                          <Upload size={18} />
                          Choose Image
                        </label>
                        {productData.image && <span className="file-name">{productData.image.name}</span>}
                      </div>
                    </div>
                    <button onClick={createProduct} disabled={loading} className="button button-primary button-full">
                      {loading ? (
                        <>
                          <span className="spinner"></span>
                          Creating...
                        </>
                      ) : (
                        "Create Product"
                      )}
                    </button>
                  </div>
                )}
                {/* View Product Form */}
                {activeOperation === "view" && (
                  <div>
                    <div className="form-group">
                      <label className="form-label">
                        Product ID <span className="required">*</span>
                      </label>
                      <div className="product-id-container">
                        <input
                          type="text"
                          value={productId}
                          onChange={(e) => setProductId(e.target.value)}
                          className="form-input product-id-input"
                          placeholder="Enter product ID"
                        />
                        <button onClick={fetchProductById} disabled={loading} className="button button-secondary">
                          {loading ? "Fetching..." : "Fetch"}
                        </button>
                      </div>
                    </div>
                    {fetchedProduct && (
                      <div className="details-container">
                        <h3 className="details-title">Product Details</h3>
                        <div className="details-grid">
                          <div className="detail-row">
                            <span className="detail-label">ID:</span>
                            <span className="detail-value">{fetchedProduct.productId}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Name:</span>
                            <span className="detail-value">{fetchedProduct.name}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Description:</span>
                            <span className="detail-value">{fetchedProduct.description}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Price:</span>
                            <span className="detail-value">${fetchedProduct.price}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Category:</span>
                            <span className="detail-value">
                              {categories.find((c) => c._id === fetchedProduct.category)?.categoryName ||
                                fetchedProduct.category}
                            </span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Tags:</span>
                            <span className="detail-value">
                              {String(fetchedProduct.tags || "")
                                .split(",")
                                .filter(Boolean) // Remove empty strings resulting from split (e.g., if tags is "")
                                .map((tag) => tag.trim())
                                .join(", ") || "N/A"}
                            </span>
                          </div>
                          {fetchedProduct.imageUrl && (
                            <div className="detail-row">
                              <span className="detail-label">Image:</span>
                              <img
                                src={`https://imperia-backend.onrender.com/${fetchedProduct.imageUrl}`}
                                alt={fetchedProduct.name}
                                className="detail-image"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Update Product Form */}
                {activeOperation === "update" && (
                  <div>
                    <div className="form-group">
                      <label className="form-label">
                        Product ID <span className="required">*</span>
                      </label>
                      <div className="product-id-container">
                        <input
                          type="text"
                          value={productId}
                          onChange={(e) => setProductId(e.target.value)}
                          className="form-input product-id-input"
                          placeholder="Enter product ID"
                        />
                        <button onClick={fetchProductById} disabled={loading} className="button button-secondary">
                          {loading ? "Fetching..." : "Fetch"}
                        </button>
                      </div>
                    </div>
                    {fetchedProduct && (
                      <div>
                        <div className="current-product-info">
                          <h3 className="current-product-title">Current Product Details:</h3>
                          <p className="current-product-detail">
                            <strong>Name:</strong> {fetchedProduct.name}
                          </p>
                          <p className="current-product-detail">
                            <strong>Price:</strong> ${fetchedProduct.price}
                          </p>
                          <p className="current-product-detail">
                            <strong>Category:</strong>{" "}
                            {categories.find((c) => c._id === fetchedProduct.category)?.categoryName ||
                              fetchedProduct.category}
                          </p>
                          <p className="current-product-detail">
                            <strong>Tags:</strong>{" "}
                            {String(fetchedProduct.tags || "")
                              .split(",")
                              .filter(Boolean)
                              .map((tag) => tag.trim())
                              .join(", ") || "N/A"}
                          </p>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Product Name</label>
                          <input
                            type="text"
                            name="name"
                            value={productData.name}
                            onChange={handleProductInputChange}
                            className="form-input"
                            placeholder="Leave empty to keep current value"
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Description</label>
                          <textarea
                            name="description"
                            value={productData.description}
                            onChange={handleProductInputChange}
                            rows={4}
                            className="form-textarea"
                            placeholder="Leave empty to keep current value"
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Tags (comma-separated)</label>
                          <textarea
                            name="tags"
                            value={productData.tags}
                            onChange={handleProductInputChange}
                            rows={4}
                            className="form-textarea"
                            placeholder="Leave empty to keep current value"
                          />
                        </div>
                        <div className="form-grid">
                          <div className="form-group">
                            <label className="form-label">Price</label>
                            <input
                              type="number"
                              name="price"
                              value={productData.price}
                              onChange={handleProductInputChange}
                              step="0.01"
                              className="form-input"
                              placeholder="Leave empty to keep current value"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Category</label>
                            <select
                              name="category"
                              value={productData.category}
                              onChange={handleProductInputChange}
                              className="form-select"
                            >
                              <option value="">Keep current category</option>
                              {categories.map((cat) => (
                                <option key={cat._id} value={cat._id}>
                                  {cat.categoryName}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Update Image</label>
                          <div className="file-upload-container">
                            <input
                              type="file"
                              onChange={handleProductImageChange}
                              accept="image/*"
                              style={{ display: "none" }}
                              id="image-upload-update"
                            />
                            <label htmlFor="image-upload-update" className="file-upload-label">
                              <Upload size={18} />
                              Choose New Image
                            </label>
                            {productData.image && <span className="file-name">{productData.image.name}</span>}
                          </div>
                        </div>
                        <button
                          onClick={updateProduct}
                          disabled={loading}
                          className="button button-warning button-full"
                        >
                          {loading ? (
                            <>
                              <span className="spinner"></span>
                              Updating...
                            </>
                          ) : (
                            "Update Product"
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {/* Delete Product Form */}
                {activeOperation === "delete" && (
                  <div>
                    <div className="form-group">
                      <label className="form-label">
                        Product ID <span className="required">*</span>
                      </label>
                      <div className="product-id-container">
                        <input
                          type="text"
                          value={productId}
                          onChange={(e) => setProductId(e.target.value)}
                          className="form-input product-id-input"
                          placeholder="Enter product ID"
                        />
                        <button onClick={fetchProductById} disabled={loading} className="button button-secondary">
                          {loading ? "Fetching..." : "Fetch"}
                        </button>
                      </div>
                    </div>
                    {fetchedProduct && (
                      <div className="delete-confirmation">
                        <h3 className="delete-title">Confirm Deletion</h3>
                        <div>
                          <p className="delete-warning">You are about to delete the following product:</p>
                          <div className="delete-product-info">
                            <p>
                              <strong>Name:</strong> {fetchedProduct.name}
                            </p>
                            <p>
                              <strong>ID:</strong> {fetchedProduct.productId}
                            </p>
                            <p>
                              <strong>Description:</strong> ${fetchedProduct.description}
                            </p>
                          </div>
                        </div>
                        <p className="delete-warning">This action cannot be undone!</p>
                        <button onClick={deleteProduct} disabled={loading} className="button button-danger button-full">
                          {loading ? (
                            <>
                              <span className="spinner"></span>
                              Deleting...
                            </>
                          ) : (
                            "Confirm Delete Product"
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            {/* Category Forms */}
            {activeSection === "category" && (
              <>
                {/* Create Category Form */}
                {activeOperation === "create" && (
                  <div>
                    <div className="form-group">
                      <label className="form-label">
                        Category Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="categoryName"
                        value={categoryData.categoryName}
                        onChange={handleCategoryInputChange}
                        className="form-input"
                        placeholder="Enter category name"
                      />
                    </div>
                    <button onClick={createCategory} disabled={loading} className="button button-primary button-full">
                      {loading ? (
                        <>
                          <span className="spinner"></span>
                          Creating...
                        </>
                      ) : (
                        "Create Category"
                      )}
                    </button>
                  </div>
                )}
                {/* View Category Form */}
                {activeOperation === "view" && (
                  <div>
                    <div className="form-group">
                      <label className="form-label">
                        Category ID <span className="required">*</span>
                      </label>
                      <div className="product-id-container">
                        <input
                          type="text"
                          value={categoryId}
                          onChange={(e) => setCategoryId(e.target.value)}
                          className="form-input product-id-input"
                          placeholder="Enter category ID"
                        />
                        <button onClick={fetchCategoryById} disabled={loading} className="button button-secondary">
                          {loading ? "Fetching..." : "Fetch"}
                        </button>
                      </div>
                    </div>
                    {fetchedCategory && (
                      <div className="details-container">
                        <h3 className="details-title">Category Details</h3>
                        <div className="details-grid">
                          <div className="detail-row">
                            <span className="detail-label">ID:</span>
                            <span className="detail-value">{fetchedCategory.categoryId}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Name:</span>
                            <span className="detail-value">{fetchedCategory.categoryName}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Created At:</span>
                            <span className="detail-value">{new Date(fetchedCategory.createdAt).toLocaleString()}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Updated At:</span>
                            <span className="detail-value">{new Date(fetchedCategory.updatedAt).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Update Category Form */}
                {activeOperation === "update" && (
                  <div>
                    <div className="form-group">
                      <label className="form-label">
                        Category ID <span className="required">*</span>
                      </label>
                      <div className="product-id-container">
                        <input
                          type="text"
                          value={categoryId}
                          onChange={(e) => setCategoryId(e.target.value)}
                          className="form-input product-id-input"
                          placeholder="Enter category ID"
                        />
                        <button onClick={fetchCategoryById} disabled={loading} className="button button-secondary">
                          {loading ? "Fetching..." : "Fetch"}
                        </button>
                      </div>
                    </div>
                    {fetchedCategory && (
                      <div>
                        <div className="form-group">
                          <label className="form-label">Category Name</label>
                          <input
                            type="text"
                            name="categoryName"
                            value={categoryData.categoryName}
                            onChange={handleCategoryInputChange}
                            className="form-input"
                            placeholder="Enter new category name"
                          />
                        </div>
                        <button
                          onClick={updateCategory}
                          disabled={loading}
                          className="button button-warning button-full"
                        >
                          {loading ? (
                            <>
                              <span className="spinner"></span>
                              Updating...
                            </>
                          ) : (
                            "Update Category"
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {/* Delete Category Form */}
                {activeOperation === "delete" && (
                  <div>
                    <div className="form-group">
                      <label className="form-label">
                        Category ID <span className="required">*</span>
                      </label>
                      <div className="product-id-container">
                        <input
                          type="text"
                          value={categoryId}
                          onChange={(e) => setCategoryId(e.target.value)}
                          className="form-input product-id-input"
                          placeholder="Enter category ID"
                        />
                        <button onClick={fetchCategoryById} disabled={loading} className="button button-secondary">
                          {loading ? "Fetching..." : "Fetch"}
                        </button>
                      </div>
                    </div>
                    {fetchedCategory && (
                      <div className="delete-confirmation">
                        <h3 className="delete-title">Confirm Deletion</h3>
                        <div>
                          <p className="delete-warning">You are about to delete the following category:</p>
                          <div className="delete-product-info">
                            <p>
                              <strong>Name:</strong> {fetchedCategory.categoryName}
                            </p>
                            <p>
                              <strong>ID:</strong> {fetchedCategory.categoryId}
                            </p>
                          </div>
                        </div>
                        <p className="delete-warning">This action cannot be undone!</p>
                        <button
                          onClick={deleteCategory}
                          disabled={loading}
                          className="button button-danger button-full"
                        >
                          {loading ? (
                            <>
                              <span className="spinner"></span>
                              Deleting...
                            </>
                          ) : (
                            "Confirm Delete Category"
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            {/* News Forms */}
            {activeSection === "news" && (
              <>
                {/* Create News Form */}
                {activeOperation === "create" && (
                  <div>
                    <div className="form-group">
                      <label className="form-label">
                        Headline <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="headline"
                        value={newsData.headline}
                        onChange={handleNewsInputChange}
                        className="form-input"
                        placeholder="Enter news headline"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        Content <span className="required">*</span>
                      </label>
                      <textarea
                        name="description"
                        value={newsData.description}
                        onChange={handleNewsInputChange}
                        rows={4}
                        className="form-textarea"
                        placeholder="Enter news description"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">News Photo (Optional)</label>
                      <div className="file-upload-container">
                        <input
                          type="file"
                          onChange={handleNewsPhotoChange}
                          accept="image/*"
                          style={{ display: "none" }}
                          id="news-photo-upload"
                        />
                        <label htmlFor="news-photo-upload" className="file-upload-label">
                          <Upload size={18} />
                          Choose Photo
                        </label>
                        {newsData.photo && <span className="file-name">{newsData.photo.name}</span>}
                      </div>
                    </div>
                    <button onClick={createNews} disabled={loading} className="button button-primary button-full">
                      {loading ? (
                        <>
                          <span className="spinner"></span>
                          Creating...
                        </>
                      ) : (
                        "Create News"
                      )}
                    </button>
                  </div>
                )}
                {/* View News Form */}
                {activeOperation === "view" && (
                  <div>
                    <div className="form-group">
                      <label className="form-label">
                        News ID <span className="required">*</span>
                      </label>
                      <div className="product-id-container">
                        <input
                          type="text"
                          value={newsId}
                          onChange={(e) => setNewsId(e.target.value)}
                          className="form-input product-id-input"
                          placeholder="Enter news ID"
                        />
                        <button onClick={fetchNewsById} disabled={loading} className="button button-secondary">
                          {loading ? "Fetching..." : "Fetch"}
                        </button>
                      </div>
                    </div>
                    {fetchedNews && (
                      <div className="details-container">
                        <h3 className="details-title">News Details</h3>
                        <div className="details-grid">
                          <div className="detail-row">
                            <span className="detail-label">ID:</span>
                            <span className="detail-value">{fetchedNews.newsId}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Headline:</span>
                            <span className="detail-value">{fetchedNews.headline}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Content:</span>
                            <span className="detail-value">{fetchedNews.description}</span>
                          </div>
                          {fetchedNews.photo && (
                            <div className="detail-row">
                              <span className="detail-label">Photo:</span>
                              <img
                                src={`https://imperia-backend.onrender.com/${fetchedNews.photo}`}
                                alt={fetchedNews.headline}
                                className="detail-image"
                              />
                            </div>
                          )}
                          <div className="detail-row">
                            <span className="detail-label">Created At:</span>
                            <span className="detail-value">{new Date(fetchedNews.createdAt).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Update News Form */}
                {activeOperation === "update" && (
                  <div>
                    <div className="form-group">
                      <label className="form-label">
                        News ID <span className="required">*</span>
                      </label>
                      <div className="product-id-container">
                        <input
                          type="text"
                          value={newsId}
                          onChange={(e) => setNewsId(e.target.value)}
                          className="form-input product-id-input"
                          placeholder="Enter news ID"
                        />
                        <button onClick={fetchNewsById} disabled={loading} className="button button-secondary">
                          {loading ? "Fetching..." : "Fetch"}
                        </button>
                      </div>
                    </div>
                    {fetchedNews && (
                      <div>
                        <div className="current-product-info">
                          <h3 className="current-product-title">Current News Details:</h3>
                          <p className="current-product-detail">
                            <strong>Headline:</strong> {fetchedNews.headline}
                          </p>
                          <p className="current-product-detail">
                            <strong>Content:</strong> {fetchedNews.description?.substring(0, 100)}...
                          </p>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Headline</label>
                          <input
                            type="text"
                            name="headline"
                            value={newsData.headline}
                            onChange={handleNewsInputChange}
                            className="form-input"
                            placeholder="Leave empty to keep current value"
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Content</label>
                          <textarea
                            name="description"
                            value={newsData.description}
                            onChange={handleNewsInputChange}
                            rows={4}
                            className="form-textarea"
                            placeholder="Leave empty to keep current value"
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Update Photo</label>
                          <div className="file-upload-container">
                            <input
                              type="file"
                              onChange={handleNewsPhotoChange}
                              accept="image/*"
                              style={{ display: "none" }}
                              id="news-photo-upload-update"
                            />
                            <label htmlFor="news-photo-upload-update" className="file-upload-label">
                              <Upload size={18} />
                              Choose New Photo
                            </label>
                            {newsData.photo && <span className="file-name">{newsData.photo.name}</span>}
                          </div>
                        </div>
                        <button onClick={updateNews} disabled={loading} className="button button-warning button-full">
                          {loading ? (
                            <>
                              <span className="spinner"></span>
                              Updating...
                            </>
                          ) : (
                            "Update News"
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {/* Delete News Form */}
                {activeOperation === "delete" && (
                  <div>
                    <div className="form-group">
                      <label className="form-label">
                        News ID <span className="required">*</span>
                      </label>
                      <div className="product-id-container">
                        <input
                          type="text"
                          value={newsId}
                          onChange={(e) => setNewsId(e.target.value)}
                          className="form-input product-id-input"
                          placeholder="Enter news ID"
                        />
                        <button onClick={fetchNewsById} disabled={loading} className="button button-secondary">
                          {loading ? "Fetching..." : "Fetch"}
                        </button>
                      </div>
                    </div>
                    {fetchedNews && (
                      <div className="delete-confirmation">
                        <h3 className="delete-title">Confirm Deletion</h3>
                        <div>
                          <p className="delete-warning">You are about to delete the following news:</p>
                          <div className="delete-product-info">
                            <p>
                              <strong>Headline:</strong> {fetchedNews.headline}
                            </p>
                            <p>
                              <strong>ID:</strong> {fetchedNews.newsId}
                            </p>
                          </div>
                        </div>
                        <p className="delete-warning">This action cannot be undone!</p>
                        <button onClick={deleteNews} disabled={loading} className="button button-danger button-full">
                          {loading ? (
                            <>
                              <span className="spinner"></span>
                              Deleting...
                            </>
                          ) : (
                            "Confirm Delete News"
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductManagementDashboard
